// End-to-end encryption between the phone and the PC (WebCrypto; works in Safari and Node).
// Must match sidecar/src/live_voice/remote/crypto.py byte for byte. See that file for the scheme.

export type Bytes = Uint8Array<ArrayBuffer>;
export const KIND_TEXT = 0;
export const KIND_BINARY = 1;

const subtle = globalThis.crypto.subtle;
const enc = new TextEncoder();

export function randomBytes(n: number): Bytes {
  return globalThis.crypto.getRandomValues(new Uint8Array(n));
}

export function concat(...parts: Uint8Array[]): Bytes {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function b64u(data: Uint8Array): string {
  let s = "";
  for (const b of data) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function unb64u(text: string): Bytes {
  const s = atob(text.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (text.length % 4)) % 4));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

async function hkdf(ikm: Bytes, salt: Bytes, info: Bytes, bits: number): Promise<Bytes> {
  const key = await subtle.importKey("raw", ikm, "HKDF", false, ["deriveBits"]);
  return new Uint8Array(await subtle.deriveBits({ name: "HKDF", hash: "SHA-256", salt, info }, key, bits));
}

export async function phoneKeypair(): Promise<{ priv: CryptoKey; pub: Bytes }> {
  const kp = (await subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"])) as CryptoKeyPair;
  return { priv: kp.privateKey, pub: new Uint8Array(await subtle.exportKey("raw", kp.publicKey)) };
}

/** Phone side: our private key, the PC's public key. */
export function pairKeys(priv: CryptoKey, cpub: Bytes, dpub: Bytes): Promise<{ c2d: Bytes; d2c: Bytes }> {
  return deriveKeys(priv, dpub, cpub, dpub);
}

/** Either side: our private key and the other side's public key. */
export async function deriveKeys(priv: CryptoKey, peerPub: Bytes, cpub: Bytes, dpub: Bytes): Promise<{ c2d: Bytes; d2c: Bytes }> {
  const peer = await subtle.importKey("raw", peerPub, { name: "ECDH", namedCurve: "P-256" }, false, []);
  const shared = new Uint8Array(await subtle.deriveBits({ name: "ECDH", public: peer }, priv, 256));
  const okm = await hkdf(shared, enc.encode("live-voice/pair/v1"), concat(cpub, dpub), 512);
  return { c2d: okm.slice(0, 32), d2c: okm.slice(32) };
}

export async function sasCode(cpub: Bytes, dpub: Bytes): Promise<string> {
  const d = new Uint8Array(await subtle.digest("SHA-256", concat(enc.encode("live-voice/sas/v1"), cpub, dpub)));
  const n = ((d[0] << 24) >>> 0) + (d[1] << 16) + (d[2] << 8) + d[3];
  return String(n % 1_000_000).padStart(6, "0");
}

function nonce(ctr: Bytes): Bytes {
  return concat(new Uint8Array(4), ctr);
}

function counterBytes(n: number): Bytes {
  const b = new Uint8Array(8);
  new DataView(b.buffer).setBigUint64(0, BigInt(n));
  return b;
}

export class LegCipher {
  private sendCtr = 0;
  private recvCtr = 0n;
  private sealChain: Promise<unknown> = Promise.resolve();
  private openChain: Promise<unknown> = Promise.resolve();

  private constructor(private readonly sendKey: CryptoKey, private readonly recvKey: CryptoKey) {}

  static async create(c2d: Bytes, d2c: Bytes, salt: Bytes, role: "client" | "desk"): Promise<LegCipher> {
    const aes = async (k: Bytes, info: string) =>
      subtle.importKey("raw", await hkdf(k, salt, enc.encode(info), 256), "AES-GCM", false, ["encrypt", "decrypt"]);
    const kc = await aes(c2d, "c2d");
    const kd = await aes(d2c, "d2c");
    return role === "client" ? new LegCipher(kc, kd) : new LegCipher(kd, kc);
  }

  /** Seals one frame. Results resolve in call order, so frames can be sent as they resolve. */
  seal(kind: number, data: Uint8Array): Promise<Bytes> {
    const ctr = counterBytes(++this.sendCtr);
    const p = this.sealChain.then(async () => {
      const ct = await subtle.encrypt({ name: "AES-GCM", iv: nonce(ctr) }, this.sendKey, concat(new Uint8Array([kind]), data));
      return concat(ctr, new Uint8Array(ct));
    });
    this.sealChain = p.catch(() => {});
    return p;
  }

  open(frame: Uint8Array): Promise<{ kind: number; data: Bytes }> {
    const p = this.openChain.then(async () => {
      if (frame.length < 25) throw new Error("frame too short");
      const ctrBytes = frame.slice(0, 8);
      const ctr = new DataView(ctrBytes.buffer).getBigUint64(0);
      if (ctr <= this.recvCtr) throw new Error("replayed or reordered frame");
      let plain: Bytes;
      try {
        plain = new Uint8Array(await subtle.decrypt({ name: "AES-GCM", iv: nonce(ctrBytes) }, this.recvKey, frame.slice(8)));
      } catch {
        throw new Error("authentication failed");
      }
      this.recvCtr = ctr;
      return { kind: plain[0], data: plain.slice(1) };
    });
    this.openChain = p.catch(() => {});
    return p;
  }
}
